import { useRef, useState, type CSSProperties } from "react";
import { Loader2, Pause, Play } from "lucide-react";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import { fillStopAt, formatTime, THUMB_SIZE } from "./audio-player.utils";

interface AudioPlayerProps {
  src: string;
  /** Durée de la piste en secondes, strictement positive : elle est annoncée par celui qui sert le
   *  fichier, et permet d'afficher le lecteur sans aller chercher le fichier. */
  durationInSeconds: number;
  /** Complète les commandes pour les lecteurs d'écran : « Écouter <trackLabel> », « Position dans
   *  <trackLabel> ». Se lit donc au milieu d'une phrase — « la note de votre professeur ». */
  trackLabel: string;
  className?: string;
}

const RANGE_CLASSES =
  "h-1.5 min-w-0 flex-1 cursor-pointer appearance-none rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-muted [&::-moz-range-thumb]:size-[var(--thumb-size)] [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary [&::-moz-range-track]:bg-transparent [&::-webkit-slider-thumb]:size-[var(--thumb-size)] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary";

// Un seul enregistrement audible à la fois : dans une liste de séances, en lancer un met l'autre en
// pause plutôt que de les superposer. Les éléments sont dans le document, ils se suffisent — pas
// besoin de tenir un registre à côté.
const pauseEveryOtherTrack = (playing: HTMLAudioElement) => {
  document.querySelectorAll("audio").forEach(other => {
    if (other !== playing) other.pause();
  });
};

// Lecteur réduit à ce dont on a besoin pour une note vocale : lire, mettre en pause, se déplacer
// dans la piste. Les contrôles natifs (`<audio controls>`) ajoutent volume, vitesse et
// téléchargement, et changent d'allure d'un navigateur à l'autre.
export const AudioPlayer = ({ src, durationInSeconds, trackLabel, className }: AudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  // Distingue l'échec du préchargement de celui d'une écoute demandée : seul le second se montre.
  const playRequested = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  // Le fichier fait foi dès qu'il est là : sa durée exacte cale la fin de la barre au millième.
  // `null` tant qu'on ne l'a pas ouvert — c'est alors la durée annoncée qui sert.
  const [measuredDuration, setMeasuredDuration] = useState<Nullable<number>>(null);
  const [isUnavailable, setIsUnavailable] = useState(false);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      playRequested.current = true;
      // Un préchargement qui a échoué laisse l'élément en erreur : il refuserait de jouer sans
      // qu'on lui redonne sa source. Le réseau a pu se rétablir entre-temps.
      if (audio.error) audio.load();
      audio.play().catch(() => setIsUnavailable(true));
    } else {
      audio.pause();
    }
  };

  const seek = (position: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = position;
    setCurrentTime(position);
  };

  if (isUnavailable) {
    return <p className={cn("text-xs text-muted-foreground", className)}>Enregistrement indisponible pour le moment.</p>;
  }

  const duration = measuredDuration ?? durationInSeconds;
  const progress = currentTime / duration;
  const fillStop = fillStopAt(progress);

  return (
    <div className={cn("flex w-full items-center gap-3 rounded-full bg-muted py-1.5 pl-1.5 pr-4", className)}>
      <button
        type="button"
        onClick={togglePlay}
        aria-label={isPlaying ? `Mettre en pause ${trackLabel}` : `Écouter ${trackLabel}`}
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-muted"
      >
        {isBuffering ? (
          <Loader2 className="size-4 animate-spin" />
        ) : isPlaying ? (
          <Pause className="size-3.5 fill-current" />
        ) : (
          // Décalé d'un cheveu : un triangle centré géométriquement paraît collé à gauche.
          <Play className="size-3.5 translate-x-px fill-current" />
        )}
      </button>

      {/* Barre de progression : dessiner la forme d'onde demanderait de télécharger et décoder
          chaque enregistrement de la liste, ou que le backend en stocke les pics à l'upload. */}
      <input
        type="range"
        min={0}
        max={duration}
        step="any"
        value={currentTime}
        onChange={event => seek(Number(event.target.value))}
        aria-label={`Position dans ${trackLabel}`}
        aria-valuetext={`${formatTime(currentTime)} sur ${formatTime(duration)}`}
        style={
          {
            "--thumb-size": `${THUMB_SIZE}px`,
            backgroundImage: `linear-gradient(to right, hsl(var(--primary)) ${fillStop}, hsl(var(--muted-foreground) / 0.35) ${fillStop})`,
          } as CSSProperties
        }
        className={RANGE_CLASSES}
      />

      {/* Position et durée ensemble, du début à la fin : un compteur seul repartait de zéro au
          lancement sans qu'on sache sur quelle longueur. La largeur est réservée pour que la barre
          ne saute pas quand la durée mesurée remplace la durée annoncée. */}
      <span className="min-w-[4.5rem] shrink-0 text-right text-xs font-medium tabular-nums text-muted-foreground">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>

      {/* `preload="none"` : la durée vient du serveur, donc une liste de séances n'a aucune raison
          d'aller chercher les fichiers. On ne télécharge un enregistrement que si on le lance. */}
      <audio
        ref={audioRef}
        src={src}
        preload="none"
        onPlay={event => {
          pauseEveryOtherTrack(event.currentTarget);
          setIsPlaying(true);
        }}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsBuffering(true)}
        onPlaying={() => setIsBuffering(false)}
        onLoadedMetadata={event => setMeasuredDuration(Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : null)}
        onTimeUpdate={event => setCurrentTime(event.currentTarget.currentTime)}
        onEnded={event => {
          // Le lecteur reste au bout, barre pleine : on voit qu'on a écouté la note jusqu'à la fin.
          // C'est le prochain « Écouter » qui le ramène à zéro — l'élément natif le fait lui-même
          // quand on le relance après la fin.
          setCurrentTime(event.currentTarget.duration);
          setIsPlaying(false);
          setIsBuffering(false);
        }}
        // Un chargement que le navigateur a lancé de lui-même — malgré `preload="none"`, il en a le
        // droit — ne condamne pas la note : le lecteur reste en place et le prochain « Écouter »
        // retente. On n'annonce l'indisponibilité qu'à quelqu'un qui a demandé à écouter.
        onError={() => {
          if (playRequested.current) setIsUnavailable(true);
        }}
      />
    </div>
  );
};
